import { Mutation } from '@apollo/client/react/components';
import { createEventData, pushToDataLayer, pushToDataLayerRespondentCreateSuccess, useToast } from '@arnold/common';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loading } from '../../components';
import SurveyForm from '../../components/Forms/SurveyForm';
import { FormResults } from '../../components/Forms/formTypes';
import {
  AnonymityLevel,
  ConversationSubjectType,
  CreateSurveyMutation,
  CreateSurveyMutationVariables,
  DatesForNewSurvey,
  useAllowedTopicGroupsQuery,
  useCreatePublicSurveyMutation,
} from '../../generated/hooks';
import { createSurveyMutation } from '../../graphql/mutations';
import {
  getAllowedTopicGroups,
  getDashboardQuery,
  getOrganizationStructure,
  getOrganizationSurveysQuery,
  getSurveyGroups,
  getUserQuery,
} from '../../graphql/queries';
import { getOperationName } from '../../lib/common';
import ee from '../../lib/eventEmitter';
import { hasTopicMissingTranslations } from '../../lib/helpers';
import i18n from '../../translations/i18n';
import { BaseSurveyProps } from './CreateSurveyScreen';

type FormProps = BaseSurveyProps & {
  datesForNewSurvey: DatesForNewSurvey;
};

export const CreateSurveyForm = ({
  subjectLabel,
  history,
  subjectId,
  subjectType,
  datesForNewSurvey,
  user,
  organization,
}: FormProps) => {
  const { addToast } = useToast();
  const [newRespondents, setNewRespondents] = useState<string[]>([]);
  const { loading, data } = useAllowedTopicGroupsQuery({
    variables: {
      languageCode: i18n.language,
    },
    fetchPolicy: 'network-only',
  });
  const [t] = useTranslation('toast');
  const [createPublicSurvey, { loading: publicSurveyLoading }] = useCreatePublicSurveyMutation();

  const handlePublicSurveySave = (endsAt?: Date) => {
    createPublicSurvey({
      variables: {
        input: {
          organizationId: user.organization!.id,
          topicId: data?.allowedTopicGroups?.find((tg) => tg.id === subjectId)?.lastValidTopic?.id!,
          language: user.organization?.primaryLanguageCode!,
          endsAt,
        },
      },
      refetchQueries: () => [
        {
          query: getDashboardQuery,
          variables: {
            languageCode: i18n.language,
          },
        },
      ],
      onCompleted: (data) => {
        ee.suppressNextModalReset();
        history.push('/overview');
        addToast(t('surveyForm:publicSurveyCreated'));
        pushToDataLayer({
          userId: user.id,
          orgId: organization?.id,
          event: 'ux.create-public-survey-success',
          ...createEventData('survey', 'createPublicSuccess', 'public survey create success'),
        });
        ee.emitModalSharePublicSurvey(data.createPublicSurvey.accessKey!, data.createPublicSurvey.topic?.name!);
      },
    });
  };

  const handleTopicOnComplete = () => {
    addToast(t('toast:surveysOverviewNewSurveyPlanned'));
    history.push('/overview');
    pushToDataLayer({
      userId: user.id,
      orgId: organization?.id,
      event: 'ux.create-survey-success',
      ...createEventData('survey', 'createSuccess', 'survey create success'),
    });

    newRespondents.map(() => pushToDataLayerRespondentCreateSuccess(user));
  };

  const handleMutationError = () => {
    addToast(t('toast:changesFailedToSave'));
    pushToDataLayer({
      userId: user.id,
      orgId: organization?.id,
      event: 'ux.create-survey-error',
      ...createEventData('survey', 'createError', 'survey create error'),
    });
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <Mutation<CreateSurveyMutation, CreateSurveyMutationVariables>
      mutation={createSurveyMutation}
      refetchQueries={[
        getOperationName(getUserQuery) || '',
        {
          query: getOrganizationStructure,
          variables: { organizationId: organization?.id },
        },
        getSurveyGroups,
        {
          query: getOrganizationSurveysQuery,
          variables: {
            languageCode: i18n.language,
            organizationId: organization?.id,
          },
        },
        {
          query: getAllowedTopicGroups,
          variables: { languageCode: i18n.language },
        },
      ]}
      onCompleted={handleTopicOnComplete}
      onError={handleMutationError}
    >
      {(createSurvey, { loading }) => {
        const handleSave = (formResults: FormResults) => {
          createSurvey({
            variables: {
              input: {
                organizationId: user.organization!.id,
                conversationSubjectId: subjectId,
                conversationSubjectType: subjectType as ConversationSubjectType,
                sendAt: formResults.sendAt,
                endsAt: formResults.endsAt,
                reminders: formResults.reminders
                  ?.slice()
                  .filter((r) => r && r.getTime() > new Date().getTime())
                  .sort((a, b) => a.getTime() - b.getTime())
                  .map((reminderAt) => ({ reminderAt })),
                newRecipientEmails: formResults.participants?.map((p) => p.trim()),
                recipientIds: formResults.whitelist
                  ? (formResults.respondents.map((respondent) => respondent.id).filter(Boolean) as string[])
                  : undefined,
                sendEmailSms: formResults.sendEmailSms,
                anonymity: {
                  allowedLevels: formResults.anonymityLevels,
                  default: formResults.anonymityLevels[0] ?? AnonymityLevel.Organization,
                },
              },
            },
          });
        };

        return (
          <SurveyForm
            loading={loading || publicSurveyLoading}
            user={user}
            isEdit={false}
            respondents={[]}
            onSave={handleSave}
            onPublicSurveySave={handlePublicSurveySave}
            subjectLabel={subjectLabel}
            surveyDates={{
              ...datesForNewSurvey,
              reminders: [datesForNewSurvey.reminderAt, datesForNewSurvey.secondReminderAt],
            }}
            hasMissingTranslations={(invitedRespondents) =>
              hasTopicMissingTranslations(
                user.organization,
                data?.allowedTopicGroups?.find((tg) => tg.id === subjectId)?.lastValidTopic,
                invitedRespondents,
              )
            }
            setNewRespondents={setNewRespondents}
            topicGroup={data?.allowedTopicGroups?.find((tg) => tg.id === subjectId)}
          />
        );
      }}
    </Mutation>
  );
};
